import React, { createContext, useContext } from 'react';

/**
 * § 365 — the region whose field errors an `ErrorSummary` announces.
 *
 * A field's error line is `role="alert"` (§ 302), so each announces as it appears. That is
 * right for one field checked on blur, and wrong for a submit: five invalid fields raised five
 * alerts at once, and a screen reader either queued them or cut each one off. Inside this
 * scope the lines keep their text, their ink and their place in `aria-describedby`, but not the
 * live role: the summary is the one announcement, and a line is read when its field takes focus.
 * A seam at the design-system root, not a component, so `fieldHelper.tsx` can read it without
 * importing the summary.
 */
const SummarisedContext = createContext(false);

export interface ErrorSummaryScopeProps {
  children: React.ReactNode;
}

/** Wrap the form: the summary and the fields it lists. */
export const ErrorSummaryScope = ({ children }: ErrorSummaryScopeProps) => (
  <SummarisedContext.Provider value>{children}</SummarisedContext.Provider>
);

/** Whether this field sits inside an `ErrorSummaryScope`. */
export const useErrorsSummarised = (): boolean => useContext(SummarisedContext);
